<template>
  <q-item
    class="q-px-md q-py-sm"
    style="padding-left: 10px;"
    clickable
    @click="$emit('select')"
  >
    <q-item-section>
      <q-item-section>
        <q-item-label
          overline
          style="font-size: 10px"
        >
          {{ item.breadcrumbs }}
        </q-item-label>
      </q-item-section>
      <q-item class="q-pa-none q-pt-xs items-center">
        <q-item-label>
          <q-icon
            :name="item.icon || 'settings'"
            size="md"
            class="q-pr-sm"
          />
        </q-item-label>

        <q-item-section>
          <q-item-section class="q-mb-xs">
            <q-item-label style="font-size: 18px;">
              {{ item.label }}
            </q-item-label>
          </q-item-section>
          <q-item-label caption>
            {{ item.description }}
          </q-item-label>
        </q-item-section>
      </q-item>
    </q-item-section>
  </q-item>
</template>

<script>

import { adminRoot } from 'src/config'
import Model from 'src/models/model'
import Report from 'src/models/report/Report'
import { developersMenuItems } from 'src/modules/admin/menu/DevelopersMenu'
import { operationsMenuItems } from 'src/modules/admin/menu/OperationsMenu'
import { settingsMenuItems } from 'src/modules/admin/menu/SettingsMenu'

const reports = new Report({}, {
  queryName: 'reports',
  isCollection: true,
  fields: ['id', 'name', 'icon', 'description'],
  reportCategory: {
    fields: ['id', 'name']
  },
  variables: {
    limit: 100
  }
})

function breadcrumbs (route) {
  const routeElements = route.split('/')
  return _(routeElements).map((slug, index) => {
    if (slug === '') return null

    if (index === routeElements.length - 1) return null

    return _.startCase(slug)
  }).compact().value().join(' > ')
}

function isMenuItemPermitted (menuItem) {
  if (!_.isEmpty(menuItem.requiresPermission)) {
    return Model.context.isPermitted(menuItem.requiresPermission)
  } else if (!_.isEmpty(menuItem.requiresAnyPermission)) {
    return Model.context.isAnyPermitted(menuItem.requiresAnyPermission)
  }
  return true
}

let fullMenuList
async function loadFullMenuList () {
  if (Report.isViewPermitted) {
    await reports.fetch()
  }

  /**
   * @typedef {import("src/modules/admin/menu/AdminMenuMixin").MenuItem} MenuItem
   * @type {Record<string, MenuItem[]>}
   */
  const topMenuCategories = {
    operations: operationsMenuItems,
    settings: settingsMenuItems,
    developer: (Model.context.currentUser && Model.context.currentUser?.superUser ? developersMenuItems : [])
  }

  const fixedMenuItems = _(topMenuCategories).map((categories, topCategory) => {
    const permittedCategories = _.filter(categories, isMenuItemPermitted)

    return permittedCategories.map(category => {
      return _.map(category.entries, subItem => {
        if (!subItem.route) return null
        if (!isMenuItemPermitted(subItem)) return null

        const items = []
        const item = _.clone(subItem)
        item.route = adminRoot + _.startCase(topCategory) + '/' + (category.route || _.kebabCase(category.label)) + '/' + subItem.route
        item.id = item.route
        item.breadcrumbs = breadcrumbs(item.route)
        item.component = 'menu-search-component'
        items.push(item)
        return items
      })
    })
  }).flattenDeep().compact().value()

  let reportItems = []
  if (Report.isViewPermitted) {
    reportItems =
      _(reports.data).map(report => {
        const route = `/reports/${_.kebabCase(report.reportCategory?.name || 'uncategorized')}/${report.id}`

        return {
          route,
          id: route,
          label: report.name,
          icon: report.icon,
          description: report.description,
          breadcrumbs: breadcrumbs(route, report.name),
          component: 'menu-search-component'
        }
      }).compact().value()
  }

  fullMenuList = [...fixedMenuItems, ...reportItems]
}

export function resetMenuList () {
  fullMenuList = null
}

export async function menuSearch (searchTerms, count) {
  if (!fullMenuList) await loadFullMenuList()

  if (searchTerms.length === 0) return _(fullMenuList).filter(isMenuItemPermitted).take(count).value()

  const searchFields = ['label', 'description', 'route', 'breadcrumbs']
  return _(fullMenuList).filter(item => {
    return _.every(searchTerms, term => {
      return _.some(searchFields, field => {
        return (item[field] ?? '').toLowerCase().includes(term)
      })
    })
  }).filter(isMenuItemPermitted).take(count).value()
}

export default {
  name: 'MenuSearchComponent',
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  emits: ['select']
}
</script>
